// "Power right now": solar / battery / grid / house around the plan pill, with chevrons moving along each
// active link toward the receiver, and the price / next peak / usable battery stats under it.
import * as React from 'react';

import { EnergyPlanSnapshot } from '../../common';
import { formatRelative } from '../util/tallinn_time';
import { hhmm, MINUS } from '../util/format';
import {
  ACTIONS,
  ActionRun,
  CONTROLLER_FLOOR_PERCENT,
  FlowNow,
  HOUR_MS,
  SLOT_MS,
  currentPlanSlot,
  priceTier,
} from '../util/energy_model';
import { Card, Stat } from './ui/card';
import { FlowBox, FlowCorner, FlowHub, FlowLink, clampDuration, flowNodeStyle } from './ui/flow';
import { HAIRLINE, SECONDARY } from './ui/tokens';

// A link with more than 50 W on it; faster chevrons for more power.
function PowerLink(props: { corner: FlowCorner; kw: number; inflow: boolean; color: string }) {
  return (
    <FlowLink
      corner={props.corner}
      active={Math.abs(props.kw) > 0.05}
      inflow={props.inflow}
      color={props.color}
      durationS={clampDuration(3.6 - Math.abs(props.kw) * 0.4)}
    />
  );
}

const nodeValue: React.CSSProperties = { fontSize: 15, fontWeight: 600, lineHeight: 1.2 };
const nodeUnit: React.CSSProperties = { fontSize: 10, color: SECONDARY, lineHeight: 1.2 };

export function PowerFlowCard(props: {
  plan: EnergyPlanSnapshot;
  runs: ActionRun[];
  flow: FlowNow | undefined;
  now: number;
  statSize: number;
  style: React.CSSProperties;
}): React.ReactElement {
  const { plan, runs, flow, now } = props;

  const planRuns = runs.filter((r) => !r.past && r.end > now);
  const nowRun = planRuns.find((r) => r.start <= now) ?? planRuns[0];
  const nextRun = nowRun ? planRuns[planRuns.indexOf(nowRun) + 1] : undefined;

  const slot = currentPlanSlot(plan, now);
  const upcoming = plan.slots.filter((s) => s.start + SLOT_MS > now && s.start < now + 12 * HOUR_MS);
  const peak = upcoming.reduce<(typeof upcoming)[number] | undefined>(
    (m, s) => (!m || s.importPrice > m.importPrice ? s : m),
    undefined,
  );
  const floor = plan.socFloorPercent ?? CONTROLLER_FLOOR_PERCENT;
  const nominal = plan.batteryNominalKwh;

  const f = flow ?? { pvKw: 0, loadKw: 0, gridKw: 0, batteryKw: 0, socPercent: 0, source: 'plan' as const };
  const exporting = f.gridKw < -0.05;
  const gridColor = exporting ? '#1baf7a' : Math.abs(f.gridKw) > 0.05 ? '#e34948' : '#d6d5ce';
  const batteryText =
    Math.abs(f.batteryKw) < 0.05 ? 'idle' : `${f.batteryKw > 0 ? '+' : MINUS}${Math.abs(f.batteryKw).toFixed(1)} kW`;
  const tier = slot ? priceTier(slot.importPrice) : undefined;
  const stale = f.source === 'stale';

  return (
    <Card label="Power right now" raised style={{ display: 'flex', flexDirection: 'column', gap: 10, ...props.style }}>
      <FlowBox
        stale={stale}
        note={
          f.source !== 'live'
            ? { text: stale ? `stale · ${formatRelative(f.receivedAt!, now)}` : 'planned values', alert: stale }
            : undefined
        }
        links={
          <>
            <PowerLink corner="topLeft" kw={f.pvKw} inflow={true} color="#eda100" />
            <PowerLink corner="bottomLeft" kw={f.gridKw} inflow={!exporting} color={gridColor} />
            <PowerLink corner="topRight" kw={f.batteryKw} inflow={f.batteryKw < 0} color="#6250d6" />
            <PowerLink corner="bottomRight" kw={f.loadKw} inflow={false} color="#eb6834" />
          </>
        }
      >
        <div
          role="img"
          aria-label={`Solar ${f.pvKw.toFixed(1)} kW`}
          style={flowNodeStyle('topLeft', { background: '#fdf5e0', border: '2px solid #eda100' })}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9a6a00"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
          <span className="num" style={nodeValue}>
            {f.pvKw.toFixed(1)}
          </span>
          <span style={nodeUnit}>kW solar</span>
        </div>

        <div
          role="img"
          aria-label={`Battery ${Math.round(f.socPercent)} %, ${batteryText}`}
          style={flowNodeStyle('topRight', { background: '#f3f1fc' })}
        >
          <svg width="70" height="70" viewBox="0 0 70 70" aria-hidden="true" style={{ position: 'absolute', inset: 0 }}>
            <circle cx="35" cy="35" r="32" fill="none" stroke="#e1ddf6" strokeWidth="4" />
            <circle
              cx="35"
              cy="35"
              r="32"
              fill="none"
              stroke="#6250d6"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={`${((Math.max(0, Math.min(100, f.socPercent)) / 100) * 201.1).toFixed(1)} 999`}
              transform="rotate(-90 35 35)"
            />
          </svg>
          <span className="num" style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.1 }}>
            {Math.round(f.socPercent)}%
          </span>
          <span className="num" style={nodeUnit}>
            {batteryText}
          </span>
        </div>

        <div
          role="img"
          aria-label={`Grid ${Math.abs(f.gridKw).toFixed(1)} kW ${exporting ? 'export' : 'import'}`}
          style={flowNodeStyle('bottomLeft', { background: '#f4f4f1', border: `2px solid ${gridColor}` })}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke={SECONDARY}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M7 22L12 2l5 20M5 8h14M4 14h16M9.5 14l5 8M14.5 14l-5 8" />
          </svg>
          <span className="num" style={nodeValue}>
            {Math.abs(f.gridKw).toFixed(1)}
          </span>
          <span style={nodeUnit}>kW {exporting ? 'export' : 'import'}</span>
        </div>

        <div
          role="img"
          aria-label={`House ${f.loadKw.toFixed(1)} kW`}
          style={flowNodeStyle('bottomRight', { background: '#fdeee7', border: '2px solid #eb6834' })}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#b4461b"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 11l9-7 9 7" />
            <path d="M5 10v10h14V10" />
            <path d="M10 20v-5h4v5" />
          </svg>
          <span className="num" style={nodeValue}>
            {f.loadKw.toFixed(1)}
          </span>
          <span style={nodeUnit}>kW house</span>
        </div>

        {nowRun && (
          <FlowHub
            tint={ACTIONS[nowRun.action].tint}
            title={ACTIONS[nowRun.action].label}
            sub={`until ${hhmm(nowRun.end)}${nextRun ? ` → ${ACTIONS[nextRun.action].label}` : ''}`}
          />
        )}
      </FlowBox>

      <div
        className="num"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 8,
          paddingTop: 10,
          borderTop: `1px solid ${HAIRLINE}`,
        }}
      >
        {slot && tier && (
          <Stat label="Price now" size={props.statSize}>
            {slot.importPrice.toFixed(1)} c{' '}
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '1px 5px',
                borderRadius: 4,
                background: tier.tint,
                verticalAlign: 2,
              }}
            >
              {tier.label}
            </span>
          </Stat>
        )}
        {peak && (
          <Stat label="Next peak" size={props.statSize}>
            {hhmm(peak.start)} · {Math.round(peak.importPrice)} c
          </Stat>
        )}
        {nominal !== undefined && flow && (
          <Stat label="Above floor" size={props.statSize} right>
            {((Math.max(0, f.socPercent - floor) / 100) * nominal).toFixed(1)} kWh
          </Stat>
        )}
      </div>
    </Card>
  );
}
